import React from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import SortableExerciseItem from './SortableExerciseItem';

const WorkoutConstructionArea = ({ exercises, onReorder, onRemove, onSelect, selectedIndex, totalDuration }) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active?.id !== over?.id) {
      const oldIndex = exercises?.findIndex(ex => ex?.id === active?.id);
      const newIndex = exercises?.findIndex(ex => ex?.id === over?.id);
      onReorder(arrayMove(exercises, oldIndex, newIndex));
    }
  };

  const calculateExerciseDuration = (exercise) => {
    const exerciseTime = exercise?.durationSeconds || (exercise?.sets * exercise?.reps * 3);
    const restTime = exercise?.restSeconds * (exercise?.sets - 1);
    return Math.ceil((exerciseTime + restTime) / 60);
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 h-[calc(100vh-250px)] flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="List" size={20} className="text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Workout Timeline</h2>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm">
            <span className="text-muted-foreground">Exercises:</span>
            <span className="font-semibold text-foreground ml-2">{exercises?.length}</span>
          </div>
          <div className="text-sm">
            <span className="text-muted-foreground">Duration:</span>
            <span className="font-semibold text-foreground ml-2">{totalDuration} min</span>
          </div>
        </div>
      </div>
      {exercises?.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Icon name="Plus" size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Add exercises from the library to start building your workout</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto space-y-2">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={exercises?.map(ex => ex?.id)} strategy={verticalListSortingStrategy}>
              {exercises?.map((exercise, index) => (
                <SortableExerciseItem
                  key={exercise?.id}
                  exercise={exercise}
                  index={index}
                  isSelected={selectedIndex === index}
                  duration={calculateExerciseDuration(exercise)}
                  onSelect={() => onSelect(index)}
                  onRemove={() => onRemove(index)}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}
      {exercises?.length > 0 && (
        <motion.div
          className="mt-4 p-4 bg-primary/10 rounded-lg border border-primary/30"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon name="Clock" size={18} className="text-primary" />
              <span className="text-sm font-medium text-foreground">Total Workout Duration</span>
            </div>
            <span className="text-xl font-bold text-primary">{totalDuration} minutes</span>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default WorkoutConstructionArea;